import { useReducer, useCallback, useEffect, useRef } from "react"
import { formatDate } from "src/utils/format-date"
import { RATINGS_INITIAL_STATE } from "../constants/ratings-initial-state"

function reducer(state, action) {
    switch (action.type) {
        case "SET_VALUE": {
            const newValue = action.payload
            const newIsEmpty = {
                ...state.isEmpty,
                [action.key]:
                    JSON.stringify(newValue) ===
                    JSON.stringify(RATINGS_INITIAL_STATE[action.key]),
            }

            return {
                ...state,
                [action.key]: newValue,
                errors: { ...state.errors, [action.key]: null },
                isEmpty: newIsEmpty,
            }
        }

        case "SET_ERROR": {
            const newValue = RATINGS_INITIAL_STATE[action.key]
            const newIsEmpty = {
                ...state.isEmpty,
                [action.key]: true,
            }
            return {
                ...state,
                [action.key]: newValue,
                errors: { ...state.errors, [action.key]: action.payload },
                isEmpty: newIsEmpty,
            }
        }

        case "SET_LOADING":
            return { ...state, isLoading: action.payload }

        case "SET_OPEN":
            return { ...state, setIsOpen: action.payload }
        default:
            return state
    }
}

export function useBoxSectionRating(cetipIsin, cpfCnpj, emissaoService) {
    const [state, dispatch] = useReducer(reducer, RATINGS_INITIAL_STATE)
    const loadedRef = useRef({ cetipIsin: null, cpfCnpj: null })

    const setValue = useCallback((key, payload) => {
        dispatch({ type: "SET_VALUE", key, payload })
    }, [])

    const setError = useCallback((key, error) => {
        dispatch({ type: "SET_ERROR", key, payload: error })
    }, [])

    const setLoading = useCallback((isLoading) => {
        dispatch({ type: "SET_LOADING", payload: isLoading })
    }, [])

    const loadDataGeneric = useCallback(
        async (requestFn, key, mapper) => {
            setLoading(true)
            try {
                const response = await requestFn()
                const { data } = response.body

                const finalData = Array.isArray(data) ? data[0] : data

                if (finalData) {
                    setValue(key, mapper(finalData))
                } else {
                    setError(key, null)
                }
            } catch (e) {
                setError(key, e)
            } finally {
                setLoading(false)
            }
        },
        [setValue, setError, setLoading]
    )

    useEffect(() => {
        if (loadedRef.current.cetipIsin === cetipIsin) return
        loadedRef.current.cetipIsin = cetipIsin

        // garante que os dados do cetip anterior sumam da tela ao mudar o cetip
        setValue("ultimoRatingEmissao", RATINGS_INITIAL_STATE.ultimoRatingEmissao)
        setValue("ultimoIndiceLiquidez", RATINGS_INITIAL_STATE.ultimoIndiceLiquidez)
        setValue("ultimoPrecosProcessados", RATINGS_INITIAL_STATE.ultimoPrecosProcessados)
        setValue("ultimoNegocio", RATINGS_INITIAL_STATE.ultimoNegocio)
        setValue("ultimoCreditoEmissao", RATINGS_INITIAL_STATE.ultimoCreditoEmissao)

        if (!cetipIsin) return

        const requests = [
            {
                key: "ultimoRatingEmissao",
                fn: () => emissaoService.getUltimoRatingEmissao(cetipIsin),
                mapper: (data) => ({
                    data_validade: formatDate(data.DataValidade),
                    fonte: data.fonte,
                    rating: data.Rating,
                }),
            },
            {
                key: "ultimoIndiceLiquidez",
                fn: () => emissaoService.getUltimoIndiceLiquidez(cetipIsin),
                mapper: (data) => ({
                    data_referencia: formatDate(data.DataReferencia),
                    indice_liquidez: data.IndiceLiquidez,
                }),
            },
            {
                key: "ultimoPrecosProcessados",
                fn: () => emissaoService.getUltimoPrecosProcessados(cetipIsin),
                mapper: (data) => ({
                    data_referencia: formatDate(data.DataReferencia),
                    spread_credito: data.SpreadCredito,
                    percentual_pupar: data.PercentualPUPAR,
                }),
            },
            {
                key: "ultimoNegocio",
                fn: () => emissaoService.getUltimoSpreadNegociados(cetipIsin),
                mapper: (data) => ({
                    data_referencia: formatDate(data.DataReferencia),
                    pupar: data.PercentualPUPAR,
                    spread_credito: data.SpreadCreditoFinal,
                }),
            },
            {
                key: "ultimoCreditoEmissao",
                fn: () =>
                    emissaoService.getUltimoEventoCreditoEmissao(cetipIsin),
                mapper: (data) => ({
                    data_referencia: formatDate(data.DataReferencia),
                    evento_credito: data.EventoCreditoEmissao,
                    score_evento: data.ScoreEvento,
                }),
            },
        ]

        requests.forEach(({ fn, key, mapper }) =>
            loadDataGeneric(fn, key, mapper)
        )
    }, [cetipIsin, emissaoService, loadDataGeneric, setValue])

    useEffect(() => {
        if (loadedRef.current.cpfCnpj === cpfCnpj) return
        loadedRef.current.cpfCnpj = cpfCnpj

        // garante que os dados do cetip anterior sumam da tela ao mudar o cetip
        setValue("ultimoRatingDevedor", RATINGS_INITIAL_STATE.ultimoRatingDevedor)
        setValue("ultimoEventoCreditoDevedor", RATINGS_INITIAL_STATE.ultimoEventoCreditoDevedor)

        if (!cpfCnpj) return

        const requests = [
            {
                key: "ultimoRatingDevedor",
                fn: () => emissaoService.getUltimoRatingDevedor(cpfCnpj),
                mapper: (data) => ({
                    data_validade: formatDate(data.DataValidade),
                    fonte: data.fonte,
                    rating: data.Rating,
                }),
            },
            {
                key: "ultimoEventoCreditoDevedor",
                fn: () => emissaoService.getUltimoEventoCreditoDevedor(cpfCnpj),
                mapper: (data) => ({
                    data_referencia: formatDate(data.DataReferencia),
                    evento_credito_devedor: data.EventoCreditoDevedor,
                    score_evento: data.ScoreEvento,
                }),
            },
        ]

        requests.forEach(({ fn, key, mapper }) =>
            loadDataGeneric(fn, key, mapper)
        )
    }, [cpfCnpj, emissaoService, loadDataGeneric, setValue])

    return { state, setValue, setError, setLoading, loadDataGeneric }
}
